(() => {
  if (customElements.get('pdp-product')) return;

  class PDPProduct extends HTMLElement {
    constructor() {
      super();
      this.onOptionChange = this.onOptionChange.bind(this);
      this.onBundleLineChange = this.onBundleLineChange.bind(this);
      this.onBundlePropertyChange = this.onBundlePropertyChange.bind(this);
      this.onBundleAvailabilityChange = this.onBundleAvailabilityChange.bind(this);
      this.onSubmit = this.onSubmit.bind(this);
      this.onNumericInput = this.onNumericInput.bind(this);
      this.onSizeGuideOpen = this.onSizeGuideOpen.bind(this);
      this.onSizeGuideClose = this.onSizeGuideClose.bind(this);
      this.onSizeGuideDialogClick = this.onSizeGuideDialogClick.bind(this);
      this.onBundleUiClick = this.onBundleUiClick.bind(this);
      this._submitting = false;
    }

    connectedCallback() {
      this.form = this.querySelector('.js-pdp-form');
      this.variantIdInput = this.querySelector('.js-pdp-variant-id');
      this.priceEl = this.querySelector('.js-pdp-price');
      this.compareEl = this.querySelector('.js-pdp-compare');
      this.atcBtn = this.querySelector('.js-pdp-atc');
      this.atcText = this.querySelector('.js-pdp-atc-text');
      this.atcPrice = this.querySelector('.js-pdp-atc-price');
      this.atcSeparator = this.querySelector('.js-pdp-atc-separator');
      this.mainImage = this.querySelector('.js-pdp-main-image');
      this.saleBadge = this.querySelector('[data-badge-type="sale"]');
      this.soldOutBadge = this.querySelector('[data-badge-type="sold_out"]');
      this.optionInputs = Array.from(this.querySelectorAll('.js-pdp-option-input'));
      this.atcErrorEl = this.querySelector('.js-pdp-atc-error');
      this.isBundle = this.dataset.bundle === 'true';

      this.textAddToCart = this.dataset.textAddToCart || '';
      this.textAdding = this.dataset.textAdding || '';
      this.textAdded = this.dataset.textAdded || '';
      this.textSoldOut = this.dataset.textSoldOut || '';
      this.textUnavailable = this.dataset.textUnavailable || '';
      this.textError = this.dataset.textError || '';
      this.textBundleNoLines = this.dataset.textBundleNoLines || '';
      this.textBundleUnavailable = this.dataset.textBundleUnavailable || this.textError;

      this._collectionHandle = this.dataset.collectionHandle || '';
      this._collectionTitle = this.dataset.collectionTitle || '';
      if (this._collectionHandle && window.DOCollectionCart) {
        window.DOCollectionCart.setActiveCollection(this._collectionHandle, this._collectionTitle);
      }

      try {
        this.variants = JSON.parse(this.dataset.variantsJson || '[]');
      } catch {
        this.variants = [];
      }

      this.optionInputs.forEach((input) => input.addEventListener('change', this.onOptionChange));
      this.form?.addEventListener('submit', this.onSubmit);
      this.addEventListener('input', this.onNumericInput);

      this.sizeGuideDialog = this.querySelector('.js-pdp-size-guide-dialog');
      this.sizeGuideOpenBtn = this.querySelector('.js-pdp-size-guide-open');
      this.sizeGuideCloseBtn = this.sizeGuideDialog?.querySelector('.js-pdp-size-guide-dialog-close') ?? null;
      if (this.sizeGuideDialog && this.sizeGuideOpenBtn) {
        this.sizeGuideOpenBtn.addEventListener('click', this.onSizeGuideOpen);
        this.sizeGuideCloseBtn?.addEventListener('click', this.onSizeGuideClose);
        this.sizeGuideDialog.addEventListener('click', this.onSizeGuideDialogClick);
      }

      const urlVariant = this.getVariantIdFromUrl();
      const variantFromUrl = urlVariant ? this.findVariantById(urlVariant) : null;
      if (this.isBundle) {
        this.addEventListener('click', this.onBundleUiClick);
        window.addEventListener('bundle:availability:change', this.onBundleAvailabilityChange);
        const variant =
          variantFromUrl ||
          this.findVariantById(this.variantIdInput?.value) ||
          (Array.isArray(this.variants) ? this.variants[0] : null);
        if (variant) {
          if (this.variantIdInput) this.variantIdInput.value = String(variant.id);
          this.updatePrice(variant);
          if (variantFromUrl) this.updateUrlVariant(variant.id);
        }
        this.initBundle();
      } else if (variantFromUrl) {
        this.applyVariantToOptions(variantFromUrl);
        this.updateFromVariant(variantFromUrl, { updateUrl: false });
      } else {
        this.syncOptionValueLabels();
        this.updateFromOptions();
      }

      this.updateOptionAvailability();
    }

    disconnectedCallback() {
      this.optionInputs?.forEach((input) => input.removeEventListener('change', this.onOptionChange));
      this.form?.removeEventListener('submit', this.onSubmit);
      this.removeEventListener('input', this.onNumericInput);
      this.sizeGuideOpenBtn?.removeEventListener('click', this.onSizeGuideOpen);
      this.sizeGuideCloseBtn?.removeEventListener('click', this.onSizeGuideClose);
      this.sizeGuideDialog?.removeEventListener('click', this.onSizeGuideDialogClick);
      if (this.isBundle) {
        this.removeEventListener('click', this.onBundleUiClick);
        window.removeEventListener('bundle:availability:change', this.onBundleAvailabilityChange);
      }
      this.destroyBundle();
    }

    // --- Bundle lines (<pdp-bundle-item> web components) ---

    getBundleLineItems() {
      return Array.from(this.querySelectorAll('pdp-bundle-item'));
    }

    isBundleReady() {
      const lines = this.getBundleLineItems();
      if (!lines.length) return false;
      return lines.every((line) => line.isReady());
    }

    getBundleSelectedVariantIds() {
      return this.getBundleLineItems()
        .map((line) => line.getResolvedVariant?.()?.id)
        .filter(Boolean);
    }

    async refreshAllBundleLines() {
      const lines = this.getBundleLineItems();
      await Promise.all(lines.map((line) => line.refreshVariants()));
    }

    preserveAllBundleSelections() {
      this.getBundleLineItems().forEach((line) => line.preserveSelections());
    }

    reconcileAllBundleLinesAfterCart() {
      this.getBundleLineItems().forEach((line) => line.reconcileAfterCart());
    }

    getBundleVariantQuantities() {
      return window.DOBundleAvailability?.getReservedQuantities?.() || {};
    }

    async syncBundleCartAvailability() {
      try {
        await window.DOBundleAvailability?.getCart?.();
        const quantities = this.getBundleVariantQuantities();
        this.getBundleLineItems().forEach((line) => {
          if (typeof line.setCartVariantQuantities === 'function') {
            line.setCartVariantQuantities(quantities);
          }
        });
        this.updateBundleAtcDisabledState();
      } catch {
        /* keep current availability */
      }
    }

    onBundleAvailabilityChange() {
      this.updateBundleAtcDisabledState();
    }

    updateBundleAtcDisabledState(options = {}) {
      if (!this.isBundle || !this.atcBtn || !this.atcText) return;
      const clearError = options.clearError !== false;

      if (clearError && this.atcErrorEl) {
        this.atcErrorEl.hidden = true;
        this.atcErrorEl.textContent = '';
      }

      const parentVariant = this.findVariantById(this.variantIdInput?.value);

      if (!this.isBundleReady()) {
        this.atcBtn.disabled = true;
        if (parentVariant?.available) {
          this.atcText.textContent = this.textAddToCart;
          this.showAtcPrice(parentVariant.price);
        }
        return;
      }

      if (!parentVariant?.available) {
        this.atcBtn.disabled = true;
        this.atcText.textContent = this.textSoldOut || this.textAddToCart;
        this.hideAtcPrice();
        return;
      }

      this.atcBtn.disabled = false;
      this.atcText.textContent = this.textAddToCart;
      this.showAtcPrice(parentVariant.price);
    }

    initBundle() {
      this.addEventListener('bundle:change', this.onBundleLineChange);
      this.querySelectorAll('.js-pdp-bundle-property').forEach((input) => {
        input.addEventListener('input', this.onBundlePropertyChange);
      });
      this.getBundleLineItems().forEach((line) => {
        if (typeof line.updateOptionAvailability === 'function') {
          line.updateOptionAvailability();
        }
      });
      void this.syncBundleCartAvailability();
      this.updateBundleAtcDisabledState();
    }

    destroyBundle() {
      this.removeEventListener('bundle:change', this.onBundleLineChange);
      this.querySelectorAll('.js-pdp-bundle-property').forEach((input) => {
        input.removeEventListener('input', this.onBundlePropertyChange);
      });
    }

    onBundleLineChange() {
      this.updateBundleAtcDisabledState();
    }

    onBundlePropertyChange() {
      this.updateBundleAtcDisabledState();
    }

    // --- Numeric-only personalization fields ---

    onNumericInput(e) {
      const input = e.target;
      if (!input.matches?.('[data-numeric-only]')) return;

      const original = input.value;
      const sanitized = original.replace(/\D/g, '');
      if (sanitized === original) return;

      const caret = input.selectionStart ?? sanitized.length;
      const removedBeforeCaret = original.slice(0, caret).replace(/[0-9]/g, '').length;
      input.value = sanitized;
      const newCaret = Math.max(0, caret - removedBeforeCaret);
      try {
        input.setSelectionRange(newCaret, newCaret);
      } catch {}
    }

    // --- Variant Options ---

    onOptionChange(e) {
      const input = e.target;
      if (!(input instanceof HTMLInputElement)) return;
      if (!input.checked) return;

      const fieldset = input.closest('[data-option-position]');
      const valueEl = fieldset?.querySelector('.js-pdp-option-value');
      if (valueEl) valueEl.textContent = input.value;

      this.updateFromOptions();
      this.updateOptionAvailability();
    }

    getSelectedOptions() {
      const fieldsets = Array.from(this.querySelectorAll('fieldset[data-option-position]'));
      return fieldsets.map((fs) => fs.querySelector('input[type="radio"]:checked')?.value ?? null);
    }

    findVariantByOptions(selectedOptions) {
      if (!Array.isArray(this.variants)) return null;
      return (
        this.variants.find((v) => Array.isArray(v?.options) && v.options.every((opt, idx) => opt === selectedOptions[idx])) || null
      );
    }

    findVariantById(id) {
      const numericId = Number(id);
      if (!numericId || !Array.isArray(this.variants)) return null;
      return this.variants.find((v) => Number(v?.id) === numericId) || null;
    }

    getVariantIdFromUrl() {
      return new URL(window.location.href).searchParams.get('variant');
    }

    escapeSelectorValue(value) {
      if (window?.CSS?.escape) return window.CSS.escape(String(value));
      return String(value).replace(/"/g, '\\"');
    }

    applyVariantToOptions(variant) {
      if (!variant || !Array.isArray(variant.options)) return;

      const fieldsets = Array.from(this.querySelectorAll('fieldset[data-option-position]'));
      fieldsets.forEach((fs, idx) => {
        const optionValue = variant.options[idx];
        if (optionValue == null) return;
        const safeValue = this.escapeSelectorValue(optionValue);
        const input = fs.querySelector(`input[type="radio"][value="${safeValue}"]`);
        if (input instanceof HTMLInputElement) input.checked = true;
      });

      this.syncOptionValueLabels();
    }

    syncOptionValueLabels() {
      const fieldsets = Array.from(this.querySelectorAll('fieldset[data-option-position]'));
      fieldsets.forEach((fs) => {
        const checked = fs.querySelector('input[type="radio"]:checked');
        const valueEl = fs.querySelector('.js-pdp-option-value');
        if (!valueEl) return;
        valueEl.textContent = checked instanceof HTMLInputElement ? checked.value : valueEl.textContent;
      });
    }

    updateUrlVariant(variantId) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', String(variantId));
      window.history.replaceState({}, '', url.toString());
    }

    updateFromVariant(variant, { updateUrl = true } = {}) {
      if (!variant) return;

      if (this.variantIdInput) this.variantIdInput.value = String(variant.id);

      this.updateImage(variant);
      this.updatePrice(variant);
      this.syncOptionValueLabels();

      if (updateUrl) this.updateUrlVariant(variant.id);

      this.dispatchEvent(new CustomEvent('pdp:variant-change', { bubbles: true, detail: { variant } }));

      if (this.isBundle) {
        this.updateBundleAtcDisabledState();
      } else {
        this.updateAddToCartState(variant);
      }
    }

    updateFromOptions() {
      const selectedOptions = this.getSelectedOptions();
      let variant = this.findVariantByOptions(selectedOptions);

      if (!variant && selectedOptions.length === 0) {
        variant =
          this.findVariantById(this.variantIdInput?.value) ||
          (Array.isArray(this.variants) ? this.variants[0] : null);
      }

      if (variant) {
        this.updateFromVariant(variant);
      } else {
        this.setAtcUnavailable();
      }
    }

    // --- Variant Availability ---

    updateOptionAvailability() {
      const fieldsets = Array.from(this.querySelectorAll('fieldset[data-option-position]'));
      if (!fieldsets.length) return;

      const selectedOptions = this.getSelectedOptions();

      fieldsets.forEach((fs, idx) => {
        const inputs = fs.querySelectorAll('input[type="radio"]');

        inputs.forEach((input) => {
          const testOptions = [...selectedOptions];
          testOptions[idx] = input.value;

          const matchingVariants = this.variants.filter((v) => {
            if (!Array.isArray(v?.options)) return false;
            return testOptions.every((opt, i) => {
              if (opt === null) return true;
              return v.options[i] === opt;
            });
          });

          const pill = input.nextElementSibling;
          if (!pill) return;

          const isUnavailable = matchingVariants.length === 0;
          const isSoldOut = matchingVariants.length > 0 && matchingVariants.every((v) => !v.available);

          input.disabled = isUnavailable;
          pill.classList.toggle('pdp__option-pill--unavailable', isUnavailable);
          pill.classList.toggle('pdp__option-pill--sold-out', isSoldOut);
        });
      });
    }

    // --- Image ---

    updateImage(variant) {
      if (!this.mainImage) return;
      const featured = variant?.featured_image;
      const src = featured?.src || featured?.url;
      if (!src) return;
      this.mainImage.setAttribute('src', src);
      this.mainImage.removeAttribute('srcset');
    }

    // --- Price ---

    updatePrice(variant) {
      const price = variant?.price != null ? Number(variant.price) : null;
      if (price != null && this.priceEl) this.priceEl.textContent = this.formatMoney(price);

      const compareAt = variant?.compare_at_price != null ? Number(variant.compare_at_price) : null;
      const hasSale = compareAt != null && price != null && compareAt > price;

      if (this.priceEl) this.priceEl.classList.toggle('pdp__price-value--sale', hasSale);

      if (this.saleBadge) {
        this.saleBadge.hidden = !hasSale;
      }

      if (!this.compareEl) return;

      if (hasSale) {
        this.compareEl.hidden = false;
        this.compareEl.textContent = this.formatMoney(compareAt);
      } else {
        this.compareEl.hidden = true;
        this.compareEl.textContent = '';
      }
    }

    // --- ATC State ---

    updateAddToCartState(variant) {
      if (!this.atcBtn || !this.atcText) return;

      if (variant?.available) {
        this.atcBtn.disabled = false;
        this.atcText.textContent = this.textAddToCart;
        this.showAtcPrice(variant.price);
      } else {
        this.atcBtn.disabled = true;
        this.atcText.textContent = this.textSoldOut || this.textAddToCart;
        this.hideAtcPrice();
      }

      if (this.soldOutBadge) {
        this.soldOutBadge.hidden = variant?.available !== false;
      }
    }

    setAtcUnavailable() {
      if (!this.atcBtn || !this.atcText) return;
      this.atcBtn.disabled = true;
      this.atcText.textContent = this.textUnavailable || this.textSoldOut || this.textAddToCart;
      this.hideAtcPrice();
    }

    showAtcPrice(cents) {
      if (!this.atcPrice || !this.atcSeparator) return;
      this.atcPrice.textContent = this.formatMoney(cents);
      this.atcPrice.hidden = false;
      this.atcSeparator.hidden = false;
    }

    hideAtcPrice() {
      if (this.atcPrice) this.atcPrice.hidden = true;
      if (this.atcSeparator) this.atcSeparator.hidden = true;
    }

    formatMoney(cents) {
      const format = window?.Shopify?.money_format || '';
      if (typeof window?.Shopify?.formatMoney === 'function' && format) {
        return window.Shopify.formatMoney(cents, format);
      }
      return '$' + (Number(cents) / 100).toFixed(2);
    }

    // --- Size Guide ---

    openSizeGuideDialog(dialog) {
      if (!(dialog instanceof HTMLDialogElement)) return;
      dialog.showModal();
      dialog.querySelectorAll('.js-pdp__size-guide-dialog-image').forEach((img) => {
        if (!(img instanceof HTMLImageElement)) return;
        img.loading = 'eager';
        if (!img.complete || img.naturalWidth === 0) {
          const src = img.currentSrc || img.src;
          if (src) {
            img.src = src;
          }
        }
      });
    }

    onSizeGuideOpen(e) {
      e.preventDefault();
      this.openSizeGuideDialog(this.sizeGuideDialog);
    }

    onSizeGuideClose() {
      if (!(this.sizeGuideDialog instanceof HTMLDialogElement)) return;
      this.sizeGuideDialog.close();
    }

    onSizeGuideDialogClick(e) {
      if (e.target === this.sizeGuideDialog) this.onSizeGuideClose();
    }

    onBundleUiClick(e) {
      const openBtn = e.target.closest('.js-pdp-bundle-size-guide-open');
      if (openBtn) {
        e.preventDefault();
        const id = openBtn.getAttribute('aria-controls');
        const dlg = id ? document.getElementById(id) : null;
        this.openSizeGuideDialog(dlg);
        return;
      }

      const closeBtn = e.target.closest('.js-pdp-bundle-size-guide-dialog .js-pdp-size-guide-dialog-close');
      if (closeBtn) {
        const dlg = closeBtn.closest('.js-pdp-bundle-size-guide-dialog');
        if (dlg instanceof HTMLDialogElement) dlg.close();
        return;
      }

      const bundleDlg = e.target.closest('.js-pdp-bundle-size-guide-dialog');
      if (e.target === bundleDlg && bundleDlg instanceof HTMLDialogElement) {
        bundleDlg.close();
      }
    }

    // --- Form Submit ---

    async onSubmit(e) {
      e.preventDefault();
      if (this._submitting || this.atcBtn?.disabled) return;

      if (this.isBundle && !this.isBundleReady()) return;

      if (this.form && !this.form.checkValidity()) {
        this.form.reportValidity();
        return;
      }

      if (this.isBundle) {
        await this.addBundleToCart();
      } else {
        await this.addToCart();
      }
    }

    async addToCart() {
      const variantId = this.variantIdInput?.value;
      if (!variantId) return;

      this._submitting = true;
      this.setAtcState('loading');

      if (this._collectionHandle) {
        try { await window.DOCollectionCart?.ensureShopifySync(); } catch {}
      }

      const formData = new FormData(this.form);
      formData.set('id', variantId);
      formData.set('sections', 'cart-drawer');

      const root = window.Shopify?.routes?.root ?? '/';

      try {
        const response = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
          body: formData,
        });

        const data = await response.json();

        if (!response.ok) {
          this.setAtcState('error', data.description || data.message || this.textError);
          return;
        }

        this.setAtcState('added');
        window.dispatchEvent(
          new CustomEvent('cart:add', {
            detail: { item: data, sections: data.sections },
          })
        );
        setTimeout(() => this.setAtcState('default'), 2500);
      } catch {
        this.setAtcState('error', this.textError);
      } finally {
        this._submitting = false;
      }
    }

    collectBundleCartProperties() {
      const properties = {};
      const selection = [];

      this.getBundleLineItems().forEach((line) => {
        const variant = line.getResolvedVariant();

        if (!variant?.available) return;

        const lineProps = {};
        line.querySelectorAll('.js-pdp-bundle-property').forEach((input) => {
          const key = input.dataset.propertyKey?.trim();
          if (!key) return;
          const val = input.value.trim();
          if (val) lineProps[key] = val;
        });

        selection.push({
          variantId: String(variant.id),
          properties: lineProps,
        });
      });

      properties._bundle_selection = JSON.stringify(selection);
      return properties;
    }

    async addBundleToCart() {
      const lines = this.getBundleLineItems();
      if (!lines.length) {
        this.setAtcState('error', this.textBundleNoLines || this.textError);
        return;
      }

      const parentVid = Number(this.variantIdInput?.value);
      if (!Number.isFinite(parentVid) || parentVid <= 0) {
        this.setAtcState('error', this.textError);
        return;
      }

      this._submitting = true;
      this.setAtcState('loading');

      this.preserveAllBundleSelections();

      await this.syncBundleCartAvailability();
      if (!this.isBundleReady()) {
        this._submitting = false;
        this.setAtcState('error', this.textBundleUnavailable);
        return;
      }

      const validation = await window.DOBundleAvailability?.validateBundleSelection?.(this.getBundleSelectedVariantIds(), 1);
      if (validation && !validation.valid) {
        this._submitting = false;
        this.setAtcState('error', this.textBundleUnavailable);
        return;
      }

      if (this._collectionHandle) {
        try { await window.DOCollectionCart?.ensureShopifySync(); } catch {}
      }

      const properties = this.collectBundleCartProperties();
      const root = window.Shopify?.routes?.root ?? '/';

      try {
        const response = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({
            items: [{ id: parentVid, quantity: 1, properties }],
            sections: 'cart-drawer',
          }),
        });

        const data = await response.json();

        if (!response.ok) {
          await this.syncBundleCartAvailability();
          this.updateBundleAtcDisabledState();
          this.setAtcState('error', data.description || data.message || this.textError);
          return;
        }

        this.setAtcState('added');
        const lineItem = Array.isArray(data.items) ? data.items[0] : data;
        window.dispatchEvent(
          new CustomEvent('cart:add', {
            detail: { item: lineItem, items: data.items, sections: data.sections },
          })
        );

        await this.syncBundleCartAvailability();
        this.updateBundleAtcDisabledState();
        setTimeout(() => this.setAtcState('default'), 2500);
      } catch {
        this.setAtcState('default');
      } finally {
        this._submitting = false;
      }
    }

    setAtcState(state, message = '') {
      if (!this.atcBtn || !this.atcText) return;

      this.atcBtn.classList.remove('is-loading', 'is-added');

      if (this.atcErrorEl) {
        this.atcErrorEl.hidden = true;
        this.atcErrorEl.textContent = '';
      }

      if (state === 'loading') {
        this.atcBtn.classList.add('is-loading');
        this.atcBtn.disabled = true;
        if (this.textAdding) this.atcText.textContent = this.textAdding;
        this.hideAtcPrice();
        return;
      }

      if (state === 'added') {
        this.atcBtn.classList.add('is-added');
        this.atcBtn.disabled = false;
        if (this.textAdded) this.atcText.textContent = this.textAdded;
        this.hideAtcPrice();
        return;
      }

      if (state === 'error') {
        if (this.atcErrorEl && message) {
          this.atcErrorEl.textContent = message;
          this.atcErrorEl.hidden = false;
        }
      }

      const variant = this.isBundle
        ? this.findVariantById(this.variantIdInput?.value)
        : this.findVariantByOptions(this.getSelectedOptions());

      if (this.isBundle) {
        this.updateBundleAtcDisabledState({ clearError: state !== 'error' });
      } else if (variant) {
        this.updateAddToCartState(variant);
      } else {
        this.atcBtn.disabled = false;
        this.atcText.textContent = this.textAddToCart;
        const currentVariant = this.findVariantById(this.variantIdInput?.value);
        if (currentVariant) this.showAtcPrice(currentVariant.price);
      }
    }
  }

  customElements.define('pdp-product', PDPProduct);
})();